import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { format, differenceInMinutes, differenceInDays, parseISO, isToday, startOfDay, addDays, startOfWeek } from 'date-fns';
import { employeeApi, locationApi, positionApi, attendanceApi, shiftApi, holidayRequestApi } from '../services/api';
import { Employee, Location, Position, AttendanceRecord } from '../types';
import { useLocationContext } from '../components/Layout';
import { AlertCircle, Clock, AlertTriangle, Calendar, User, Loader2, UserPlus, Plus, ClipboardList } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWeekStartDay } from '../hooks/useWeekStartDay';

// Helper function to extract timezone-naive time from datetime string
const extractTimezoneNaiveTime = (datetimeString: string | null | undefined): string => {
  if (!datetimeString) return '';
  try {
    const timePart = datetimeString.split('T')[1];
    if (!timePart) return '';
    const [hours, minutes] = timePart.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  } catch (error) {
    console.warn('Error extracting timezone-naive time:', error);
    return '';
  }
};

// Loading spinner component
const LoadingSpinner: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <Loader2 className={`animate-spin text-gray-400`} size={size} />
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const weekStartsOn = useWeekStartDay(); // Universal week start day setting
  const { selectedLocationId, locations } = useLocationContext();
  
  // Basic data (for employee list and visa alerts)
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [locationsList, setLocationsList] = useState<Location[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loadingBasicData, setLoadingBasicData] = useState(true);

  // Independent loading states for each section
  const [loadingTotalEmployees, setLoadingTotalEmployees] = useState(true);
  const [loadingPresentToday, setLoadingPresentToday] = useState(true);
  const [loadingPendingHolidays, setLoadingPendingHolidays] = useState(true);
  const [loadingAbsentAlerts, setLoadingAbsentAlerts] = useState(true);
  const [loadingMissedClockoutAlerts, setLoadingMissedClockoutAlerts] = useState(true);
  const [loadingVisaAlerts, setLoadingVisaAlerts] = useState(true);

  // Independent data states for each section
  const [totalEmployeesCount, setTotalEmployeesCount] = useState<number>(0);
  const [presentTodayCount, setPresentTodayCount] = useState<number>(0);
  const [pendingHolidayRequestsCount, setPendingHolidayRequestsCount] = useState<number>(0);
  const [absentAlerts, setAbsentAlerts] = useState<Array<{ employee: Employee; date: string; shift: any }>>([]);
  const [missedClockoutAlerts, setMissedClockoutAlerts] = useState<Array<{ employee: Employee; record: AttendanceRecord; date: string; hoursClocked: string }>>([]);
  const [visaAlerts, setVisaAlerts] = useState<Array<{ employee: Employee; visaNumber: string; visaExpiryDate: string; daysUntilExpiry: number }>>([]);

  // Fetch basic data (employees, locations, positions) - for employee list and visa alerts
  useEffect(() => {
    const fetchBasicData = async () => {
      try {
        setLoadingBasicData(true);
        const [employeesData, locationsData, positionsData] = await Promise.all([
          employeeApi.getAll(),
          locationApi.getAll(),
          positionApi.getAll()
        ]);
        
        setEmployees(employeesData);
        setLocationsList(locationsData);
        setPositions(positionsData);
      } catch (error) {
        console.error('Error fetching basic dashboard data:', error);
      } finally {
        setLoadingBasicData(false);
      }
    };

    fetchBasicData();
  }, []);

  // Calculate Total Employees Count from existing employees state
  useEffect(() => {
    if (!loadingBasicData && employees.length > 0) {
      setTotalEmployeesCount(employees.length);
      setLoadingTotalEmployees(false);
    } else if (!loadingBasicData) {
      setLoadingTotalEmployees(false);
    }
  }, [employees, loadingBasicData]);

  // Independent fetch: Present Today Count
  useEffect(() => {
    const fetchPresentToday = async () => {
      try {
        setLoadingPresentToday(true);
        const today = format(new Date(), 'yyyy-MM-dd');
        
        const attendanceParams: any = {
          startDate: today,
          endDate: today
        };
        if (selectedLocationId) {
          attendanceParams.locationId = selectedLocationId;
        }
        
        const todayData = await attendanceApi.getAll(attendanceParams);
        const clockedInEmployeeIds = new Set<number>();
        todayData.forEach((record) => {
          if (record.clockInTime && !record.deletedAt) {
            const recordDate = format(new Date(record.clockInTime), 'yyyy-MM-dd');
            if (recordDate === today) {
              clockedInEmployeeIds.add(record.employeeId);
            }
          }
        });
        setPresentTodayCount(clockedInEmployeeIds.size);
      } catch (error) {
        console.error('Error fetching present today count:', error);
      } finally {
        setLoadingPresentToday(false);
      }
    };

    fetchPresentToday();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchPresentToday, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [selectedLocationId]);

  // Independent fetch: Pending Holiday Requests Count
  useEffect(() => {
    const fetchPendingHolidays = async () => {
      try {
        setLoadingPendingHolidays(true);
        const requests = await holidayRequestApi.getAll();
        const pendingCount = requests.filter(request => 
          request.status && request.status.toLowerCase() === 'pending'
        ).length;
        setPendingHolidayRequestsCount(pendingCount);
      } catch (error) {
        console.error('Error fetching pending holiday requests:', error);
      } finally {
        setLoadingPendingHolidays(false);
      }
    };

    fetchPendingHolidays();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchPendingHolidays, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);


  // Helper function to calculate hours since clock in
  const calculateHoursSinceClockIn = useCallback((clockInTime: string | null | undefined) => {
    if (!clockInTime) return { formatted: '0:00', totalHours: 0 };
    
    const clockIn = new Date(clockInTime);
    const now = new Date();
    const totalMinutes = differenceInMinutes(now, clockIn);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return {
      formatted: `${hours}:${minutes.toString().padStart(2, '0')}`,
      totalHours: hours
    };
  }, []);

  // Independent fetch: Absent Alerts
  useEffect(() => {
    const fetchAbsentAlerts = async () => {
      if (!isToday(new Date())) {
        setLoadingAbsentAlerts(false);
        setAbsentAlerts([]);
        return;
      }

      // Wait for employees to be loaded
      if (loadingBasicData || employees.length === 0) {
        return;
      }

      try {
        setLoadingAbsentAlerts(true);
        const today = format(new Date(), 'yyyy-MM-dd');
        const now = new Date();
        
        // Fetch today's attendance and today's shifts independently (reuse employees state)
        const [todayAttendanceData, todayShifts] = await Promise.all([
          (async () => {
            const attendanceParams: any = {
              startDate: today,
              endDate: today
            };
            if (selectedLocationId) {
              attendanceParams.locationId = selectedLocationId;
            }
            return await attendanceApi.getAll(attendanceParams);
          })(),
          (async () => {
            const shiftParams: any = {
              startDate: today,
              endDate: today
            };
            if (selectedLocationId) {
              shiftParams.locationId = selectedLocationId;
            }
            return await shiftApi.getAll(shiftParams);
          })()
        ]);

        // Process absent alerts
        const absentEmployees: Array<{ employee: Employee; date: string; shift: any }> = [];
        const todayClockIns = new Map<number, AttendanceRecord>();
        
        todayAttendanceData.forEach((record) => {
          if (record.clockInTime && !record.clockOutTime && !record.deletedAt) {
            const existing = todayClockIns.get(record.employeeId);
            if (!existing || new Date(record.clockInTime) > new Date(existing.clockInTime)) {
              todayClockIns.set(record.employeeId, record);
            }
          }
        });

        const employeeMap = new Map<number, Employee>();
        employees.forEach((emp) => {
          employeeMap.set(emp.id, emp);
        });

        todayShifts.forEach((shift: any) => {
          const employeeId = shift.employeeId || (shift.userId ? parseInt(shift.userId, 10) : null);
          if (!employeeId) return;

          if (selectedLocationId && shift.locationId !== selectedLocationId) {
            return;
          }

          const employee = employeeMap.get(employeeId);
          if (!employee) return;

          const attendance = todayClockIns.get(employeeId);
          
          if (!attendance) {
            const shiftStart = new Date(shift.startTime);
            const minutesSinceStart = differenceInMinutes(now, shiftStart);
            
            if (minutesSinceStart >= 60) {
              absentEmployees.push({
                employee,
                date: today,
                shift
              });
            }
          }
        });

        setAbsentAlerts(absentEmployees);
      } catch (error) {
        console.error('Error fetching absent alerts:', error);
      } finally {
        setLoadingAbsentAlerts(false);
      }
    };

    fetchAbsentAlerts();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchAbsentAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [selectedLocationId, employees, loadingBasicData]);

  // Independent fetch: Missed Clockout Alerts
  useEffect(() => {
    const fetchMissedClockoutAlerts = async () => {
      // Wait for employees to be loaded
      if (loadingBasicData || employees.length === 0) {
        return;
      }

      try {
        setLoadingMissedClockoutAlerts(true);
        const thirtyDaysAgo = format(addDays(new Date(), -30), 'yyyy-MM-dd');
        const today = format(new Date(), 'yyyy-MM-dd');
        
        // Fetch attendance records independently (reuse employees state)
        const allRecords = await (async () => {
          const params: any = {
            startDate: thirtyDaysAgo,
            endDate: today
          };
          if (selectedLocationId) {
            params.locationId = selectedLocationId;
          }
          return await attendanceApi.getAll(params);
        })();

        const clockedInRecords = allRecords.filter(
          record => record.clockInTime && !record.clockOutTime && !record.deletedAt
        );

        const attendanceMap = new Map<number, AttendanceRecord>();
        clockedInRecords.forEach((record) => {
          const existing = attendanceMap.get(record.employeeId);
          if (!existing || new Date(record.clockInTime!) > new Date(existing.clockInTime!)) {
            attendanceMap.set(record.employeeId, record);
          }
        });

        const missedClockoutEmployees: Array<{ employee: Employee; record: AttendanceRecord; date: string; hoursClocked: string }> = [];
        
        attendanceMap.forEach((record, employeeId) => {
          const hoursClocked = calculateHoursSinceClockIn(record.clockInTime);
          
          if (hoursClocked.totalHours >= 24) {
            const employee = employees.find(e => e.id === record.employeeId);
            
            if (employee) {
              const recordDate = format(new Date(record.clockInTime!), 'yyyy-MM-dd');
              missedClockoutEmployees.push({
                employee,
                record,
                date: recordDate,
                hoursClocked: hoursClocked.formatted
              });
            }
          }
        });

        setMissedClockoutAlerts(missedClockoutEmployees);
      } catch (error) {
        console.error('Error fetching missed clockout alerts:', error);
      } finally {
        setLoadingMissedClockoutAlerts(false);
      }
    };

    fetchMissedClockoutAlerts();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchMissedClockoutAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [selectedLocationId, calculateHoursSinceClockIn, employees, loadingBasicData]);

  // Independent fetch: Visa Alerts
  useEffect(() => {
    const fetchVisaAlerts = async () => {
      // Wait for employees to be loaded
      if (loadingBasicData || employees.length === 0) {
        return;
      }

      try {
        setLoadingVisaAlerts(true);
        const todayDate = new Date();
        const sevenDaysAgo = startOfDay(addDays(todayDate, -7));
        const thirtyDaysFromNow = startOfDay(addDays(todayDate, 30));

        const alerts: Array<{ employee: Employee; visaNumber: string; visaExpiryDate: string; daysUntilExpiry: number }> = [];

        employees.forEach((employee) => {
          // Skip if location filter doesn't match
          if (selectedLocationId) {
            const employeeLocationId = employee.profile?.mainLocationId;
            if (employeeLocationId !== selectedLocationId) {
              const subLocations = employee.profile?.subLocationIds || [];
              if (!subLocations.includes(selectedLocationId)) {
                return;
              }
            }
          }

          // Show visa alerts if visaExpiryDate exists
          if (employee.visaExpiryDate) {
            try {
              const expiryDate = startOfDay(parseISO(employee.visaExpiryDate));
              const daysUntilExpiry = Math.ceil(differenceInDays(expiryDate, todayDate));
              
              if (daysUntilExpiry < 0 || daysUntilExpiry <= 30) {
                alerts.push({
                  employee,
                  visaNumber: employee.visaNumber || '',
                  visaExpiryDate: employee.visaExpiryDate,
                  daysUntilExpiry
                });
              }
            } catch (error) {
              console.warn(`Error parsing visa expiry date for employee ${employee.id}:`, employee.visaExpiryDate, error);
            }
          }
        });

        // Sort visa alerts by expiry date (earliest first)
        alerts.sort((a, b) => {
          return parseISO(a.visaExpiryDate).getTime() - parseISO(b.visaExpiryDate).getTime();
        });

        setVisaAlerts(alerts);
      } catch (error) {
        console.error('Error fetching visa alerts:', error);
      } finally {
        setLoadingVisaAlerts(false);
      }
    };

    fetchVisaAlerts();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchVisaAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [selectedLocationId, employees, loadingBasicData]);


  if (loadingBasicData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div className="text-center">
          <LoadingSpinner size={32} />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const recentEmployees = employees.slice(0, 5);

  // Separate visa alerts into categories
  const expiredVisas = visaAlerts.filter(v => v.daysUntilExpiry < 0);
  const criticalVisas = visaAlerts.filter(v => v.daysUntilExpiry >= 0 && v.daysUntilExpiry <= 7);
  const warningVisas = visaAlerts.filter(v => v.daysUntilExpiry > 7 && v.daysUntilExpiry <= 30);

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left Sidebar - Alerts */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-full overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Alerts</h2>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-6 min-h-0">
          {/* Absent Alerts */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-3 flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <h3 className="font-medium text-gray-900">Absent Today</h3>
              {!loadingAbsentAlerts && absentAlerts.length > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                  {absentAlerts.length}
                </span>
              )}
            </div>
            <div className="border border-gray-200 rounded-lg bg-gray-50 h-48 overflow-y-auto">
              {loadingAbsentAlerts ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-2 px-3">
                  <LoadingSpinner size={16} />
                  <span>Loading...</span>
                </div>
              ) : absentAlerts.length > 0 ? (
                <div className="p-2 space-y-2">
                  {absentAlerts.map(({ employee, date }) => (
                    <div
                      key={`absent-${employee.id}-${date}`}
                      className="p-3 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                    >
                      <div 
                        className="font-medium text-gray-900 cursor-pointer hover:text-blue-600 hover:underline"
                        onClick={() => window.open(`/employees/${employee.id}?tab=notes`, '_blank')}
                        title="View employee notes"
                      >
                        {employee.firstName} {employee.lastName}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {format(new Date(date), 'MMM d, yyyy')}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {/* Missed Clockout Alerts */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-3 flex-shrink-0">
              <Clock className="h-5 w-5 text-red-600" />
              <h3 className="font-medium text-gray-900">Missed Clockout</h3>
              {!loadingMissedClockoutAlerts && missedClockoutAlerts.length > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                  {missedClockoutAlerts.length}
                </span>
              )}
            </div>
            <div className="border border-gray-200 rounded-lg bg-gray-50 h-48 overflow-y-auto">
              {loadingMissedClockoutAlerts ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-2 px-3">
                  <LoadingSpinner size={16} />
                  <span>Loading...</span>
                </div>
              ) : missedClockoutAlerts.length > 0 ? (
                <div className="p-2 space-y-2">
                  {missedClockoutAlerts.map(({ employee, record, date, hoursClocked }) => {
                    const clockInTime = extractTimezoneNaiveTime(record.clockInTime);
                    const dateStr = format(new Date(date), 'yyyy-MM-dd');
                    const recordDate = new Date(date);
                    const weekStartDate = startOfWeek(recordDate, { weekStartsOn });
                    const weekStr = format(weekStartDate, 'yyyy-MM-dd');
                    return (
                      <div
                        key={`missed-${record.id}`}
                        onClick={() => window.open(`/attendance?week=${weekStr}&date=${dateStr}&recordId=${record.id}`, '_blank')}
                        className="p-3 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors cursor-pointer"
                      >
                        <div className="font-medium text-gray-900">
                          {employee.firstName} {employee.lastName}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Clocked in: {clockInTime} on {format(new Date(date), 'MMM d, yyyy')}
                        </div>
                        <div className="text-xs text-red-700 font-medium mt-1">
                          Hours clocked: {hoursClocked} (24+ hours - missed clock out)
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="w-full max-w-[82%] mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-neutral-800">Dashboard</h1>
        <p className="text-neutral-600 mt-2">Welcome to your employee management dashboard</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-600 mb-1">Total Employees</p>
              {loadingTotalEmployees ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner size={20} />
                  <span className="text-gray-400">Loading...</span>
                </div>
              ) : (
                <p className="text-3xl font-bold text-neutral-800">
                  {totalEmployeesCount}
                </p>
              )}
            </div>
            <div className="text-3xl">👥</div>
          </div>
        </div>

        <div className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-600 mb-1">Present Today</p>
              {loadingPresentToday ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner size={20} />
                  <span className="text-gray-400">Loading...</span>
                </div>
              ) : (
                <p className="text-3xl font-bold text-neutral-800">
                  {presentTodayCount}
                </p>
              )}
            </div>
            <div className="text-3xl">✅</div>
          </div>
        </div>

        <div className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-600 mb-1">Pending Holiday Requests</p>
              {loadingPendingHolidays ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner size={20} />
                  <span className="text-gray-400">Loading...</span>
                </div>
              ) : (
                <p className="text-3xl font-bold text-neutral-800">
                  {pendingHolidayRequestsCount}
                </p>
              )}
            </div>
            <div className="text-3xl">📅</div>
          </div>
        </div>
      </div>

      {/* Row 2: Visa Expired and Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Position (2,1) - Visa Expired */}
        {loadingVisaAlerts ? (
          <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center shadow-sm">
            <LoadingSpinner size={32} />
            <p className="mt-4 text-neutral-600">Loading visa alerts...</p>
          </div>
        ) : (
          <>
            {expiredVisas.length > 0 && (
              <div className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm flex flex-col">
                <div className="flex items-center gap-2 mb-3 flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <h3 className="font-semibold text-red-900 text-sm">EXPIRED Visas</h3>
                  <span className="px-2 py-1 text-xs font-medium bg-red-600 text-white rounded-full">
                    {expiredVisas.length}
                  </span>
                </div>
                <div className="flex-1 overflow-y-auto min-h-0 max-h-96">
                  <div className="space-y-2 pr-2">
                    {expiredVisas.map(({ employee, visaNumber, visaExpiryDate, daysUntilExpiry }) => (
                      <div
                        key={`visa-expired-${employee.id}-${visaNumber}`}
                        onClick={() => window.open(`/employees/${employee.id}?tab=personal&scrollToVisa=true#visa-details`, '_blank')}
                        className="p-2 bg-red-50 rounded border border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
                      >
                        <div className="flex items-start gap-2">
                          <User className="h-4 w-4 text-gray-700 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-gray-900 truncate">
                              {employee.firstName} {employee.lastName}
                            </div>
                            <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                              {visaNumber && `(${visaNumber}) `}
                              Expired {Math.abs(daysUntilExpiry)} day{Math.abs(daysUntilExpiry) !== 1 ? 's' : ''} ago • {format(parseISO(visaExpiryDate), 'MMM dd, yyyy')}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {criticalVisas.length > 0 && (
              <div className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm flex flex-col">
                <div className="flex items-center gap-2 mb-3 flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <h3 className="font-semibold text-red-900 text-sm">Critical: Expiring Soon</h3>
                  <span className="px-2 py-1 text-xs font-medium bg-red-600 text-white rounded-full">
                    {criticalVisas.length}
                  </span>
                </div>
                <div className="flex-1 overflow-y-auto min-h-0 max-h-96">
                  <div className="space-y-2 pr-2">
                    {criticalVisas.map(({ employee, visaNumber, visaExpiryDate, daysUntilExpiry }) => (
                      <div
                        key={`visa-critical-${employee.id}-${visaNumber}`}
                        onClick={() => window.open(`/employees/${employee.id}?tab=personal&scrollToVisa=true#visa-details`, '_blank')}
                        className="p-2 bg-red-50 rounded border border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
                      >
                        <div className="flex items-start gap-2">
                          <User className="h-4 w-4 text-gray-700 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-gray-900 truncate">
                              {employee.firstName} {employee.lastName}
                            </div>
                            <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                              {visaNumber && `(${visaNumber}) `}
                              {daysUntilExpiry === 0 ? 'Expires today' : 
                               daysUntilExpiry === 1 ? 'Expires tomorrow' :
                               `Expires in ${daysUntilExpiry} days`} • {format(parseISO(visaExpiryDate), 'MMM dd, yyyy')}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {warningVisas.length > 0 && (
              <div className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm flex flex-col">
                <div className="flex items-center gap-2 mb-3 flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  <h3 className="font-semibold text-yellow-900 text-sm">Renewal Reminders</h3>
                  <span className="px-2 py-1 text-xs font-medium bg-yellow-600 text-white rounded-full">
                    {warningVisas.length}
                  </span>
                </div>
                <div className="flex-1 overflow-y-auto min-h-0 max-h-96">
                  <div className="space-y-2 pr-2">
                    {warningVisas.map(({ employee, visaNumber, visaExpiryDate, daysUntilExpiry }) => (
                      <div
                        key={`visa-warning-${employee.id}-${visaNumber}`}
                        onClick={() => window.open(`/employees/${employee.id}?tab=personal&scrollToVisa=true#visa-details`, '_blank')}
                        className="p-2 bg-yellow-50 rounded border border-yellow-200 cursor-pointer hover:bg-yellow-100 transition-colors"
                      >
                        <div className="flex items-start gap-2">
                          <User className="h-4 w-4 text-gray-700 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-gray-900 truncate">
                              {employee.firstName} {employee.lastName}
                            </div>
                            <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                              {visaNumber && `(${visaNumber}) `}
                              Expires in {daysUntilExpiry} days • {format(parseISO(visaExpiryDate), 'MMM dd, yyyy')}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Position (2,2-3) - Quick Actions (spans 2 columns) */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 shadow-sm md:col-span-2 lg:col-span-2">
          <h3 className="text-lg font-semibold text-neutral-800 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Existing Actions */}
            <div className="space-y-4">
              <button
                onClick={() => navigate('/employees')}
                className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-blue-50 transition-colors text-left group"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-neutral-800">Add Employee</div>
                  <div className="text-sm text-neutral-600 mt-0.5">Create a new employee profile</div>
                </div>
              </button>

              <button
                onClick={() => navigate('/scheduling')}
                className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-blue-50 transition-colors text-left group"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <Plus className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-neutral-800">Add Shift</div>
                  <div className="text-sm text-neutral-600 mt-0.5">Schedule a new shift</div>
                </div>
              </button>

              <button
                className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-blue-50 transition-colors text-left group"
                disabled
              >
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-neutral-800">Early/Late Report</div>
                  <div className="text-sm text-neutral-600 mt-0.5">View early and late arrivals</div>
                </div>
              </button>
            </div>

            {/* Right Column - Report Actions */}
            <div className="space-y-4">
              <button
                onClick={() => navigate('/task-admin')}
                className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-blue-50 transition-colors text-left group"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <ClipboardList className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-neutral-800">View Tasks</div>
                  <div className="text-sm text-neutral-600 mt-0.5">Check pending tasks</div>
                </div>
              </button>

              <button
                className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-blue-50 transition-colors text-left group"
                disabled
              >
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-neutral-800">View Labour</div>
                  <div className="text-sm text-neutral-600 mt-0.5">Check labour costs and hours</div>
                </div>
              </button>

              <button
                className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-blue-50 transition-colors text-left group"
                disabled
              >
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-neutral-800">Break Report</div>
                  <div className="text-sm text-neutral-600 mt-0.5">View break time reports</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
